import { RECT_PALETTE_SLOTS, RECT_WIDTH_SLOTS } from "../../gl/rectRenderer";
import { snapLine } from "../grid";
import {
  POSITION_LINE_WIDTH_PX,
  POSITION_TONE,
  POSITION_DIRECTION,
  TONE_COLORS,
} from "./positionConstants";

const LINE_ENDPOINT_FLOATS = 4;
const RGBA_FLOATS = 4;

// Zones span the whole pane width: one shape slot and a colour slot per tone
const ZONE_SHAPE_SLOT = 0;
const ZONE_PALETTE_SLOTS = {
  [POSITION_TONE.PROFIT]: 0,
  [POSITION_TONE.LOSS]: 1,
};
// Zone instances are in CSS px: x is ignored, y is scaled to device px
const ZONE_X_TRANSFORM = [0, 0];

/**
 * Thin solid lines of the positions in device pixels, from the right edge
 * of the pill to the price axis (moon-position-lines-spec.md). A line pinned
 * to the pane edge has no line, only its pill with ▲ / ▼.
 *
 * @param {object} params
 * @param {Array<{ tone: string, y: number, startX: number }>} params.lines - from resolvePositionScene
 * @param {{ width: number }} params.pane - CSS px
 * @param {number} params.pixelRatio
 * @param {number} params.floatsPerLine - lineRenderer.floatsPerInstance
 * @param {Object<string, { rgba: number[] }>} params.colors - theme colours
 * @returns {Float32Array}
 */
export const buildPositionLineInstances = ({ lines, pane, pixelRatio, floatsPerLine, colors }) => {
  const lineWidth = Math.max(1, Math.floor(POSITION_LINE_WIDTH_PX * pixelRatio));
  const shownLines = lines.filter((line) => !line.pin);
  const data = new Float32Array(shownLines.length * floatsPerLine);

  shownLines.forEach(({ tone, y, startX }, index) => {
    const offset = index * floatsPerLine;
    const deviceY = snapLine(y, pixelRatio, lineWidth);

    data.set([startX * pixelRatio, deviceY, pane.width * pixelRatio, deviceY], offset);
    data.set(colors[TONE_COLORS[tone].stroke].rgba, offset + LINE_ENDPOINT_FLOATS);
    data[offset + floatsPerLine - 1] = lineWidth;
  });

  return data;
};

/**
 * Semi-transparent zones beyond take profit and the loss line, up to the
 * pane edge across its whole width; drawn under the series.
 *
 * @param {object} params
 * @param {object} params.renderer - rectRenderer of the zones
 * @param {Array<{ tone: string, y: number, direction: string }>} params.zones - from resolvePositionScene
 * @param {{ width: number, height: number }} params.pane - CSS px
 * @param {number} params.pixelRatio
 * @param {number[]} params.viewport - [width, height] of the canvas, device px
 * @param {Object<string, { rgba: number[] }>} params.colors - theme colours
 */
export const drawPositionZones = ({ renderer, zones, pane, pixelRatio, viewport, colors }) => {
  const floatsPerRect = renderer.floatsPerInstance;
  const instances = new Float32Array(zones.length * floatsPerRect);
  const widths = new Float32Array(RECT_WIDTH_SLOTS);
  const palette = new Float32Array(RECT_PALETTE_SLOTS * RGBA_FLOATS);

  zones.forEach(({ tone, y, direction }, index) => {
    const edge = direction === POSITION_DIRECTION.UP ? 0 : pane.height;

    instances.set([0, y, edge, ZONE_SHAPE_SLOT, ZONE_PALETTE_SLOTS[tone]], index * floatsPerRect);
  });
  Object.entries(ZONE_PALETTE_SLOTS).forEach(([tone, slot]) => {
    palette.set(colors[TONE_COLORS[tone].zone].rgba, slot * RGBA_FLOATS);
  });
  widths[ZONE_SHAPE_SLOT] = Math.round(pane.width * pixelRatio);

  renderer.setInstances(instances);
  renderer.draw({
    viewport,
    xTransform: ZONE_X_TRANSFORM,
    yTransform: [pixelRatio, 0],
    shapes: { widths, offsets: new Float32Array(RECT_WIDTH_SLOTS), minHeight: 0 },
    palette,
  });
};
